import React, { useCallback, useMemo, useRef, useState } from "react";
import CardForm from "../../components/CardForm";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryParams } from "../../hooks/queryParams";
import queryString from "query-string";
import {
  Button,
  Container,
  Flex,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  useDisclosure,
  useToast,
  Link,
} from "@chakra-ui/react";
import RouterLink from "../../components/RouterLink";
import {
  Quotation,
  QuotationStatus,
  useCancelQuotationMutation,
  useConfirmQuotationMutation,
} from "../../generated/graphql";
import { useConfirmation } from "../../contexts/ConfirmationContext";
import { AddIcon, ChevronDownIcon } from "@chakra-ui/icons";
import TableListTitle from "../../components/TableList/TableListTitle";
import QuotationForm from "./QuotationForm";
import TableQuotation from "../../components/TableQuotation";
import { TableImperativeHandle } from "../../components/DefaultTable";
import { FormMutationType } from "../../components/FullWidthModal/interface";
import moment from "moment";
import { getThisYear } from "../../components/DateRangePicker/data";
import { IQueryParams } from "./interface";
import { IDateRangeValue } from "../../components/DateRangePicker/interface";

const ListQuotation: React.FC<{ status?: QuotationStatus }> = () => {
  const location = useLocation();

  const { pathname } = location;

  const navigate = useNavigate();

  const toast = useToast();

  const {
    isOpen: isOpenForm,
    onOpen: onOpenForm,
    onClose: onCloseForm,
  } = useDisclosure();

  const [selectedId, setSelectedId] = useState("");

  const { confirmation, setIsLoading, close } = useConfirmation();

  const { queryParams } = useQueryParams<{
    page?: string;
    key?: string;
    status?: QuotationStatus;
    dateRange?: string;
  }>();

  const onPageChange = useCallback(
    (page: number) => {
      const url = queryString.stringifyUrl({
        url: pathname,
        query: {
          ...queryParams,
          page,
        },
      });

      navigate(url);
    },
    [queryParams, navigate, pathname]
  );

  const onFilterSubmit = useCallback(
    (params: IQueryParams) => {
      const url = queryString.stringifyUrl({
        url: pathname,
        query: {
          ...queryParams,
          ...params,
          page: 1,
        },
      });
      navigate(url);
    },
    [queryParams, navigate, pathname]
  );

  const [confirmQuotationMutation] = useConfirmQuotationMutation();

  const onConfirm = useCallback(
    (row: Quotation) => {
      confirmation({
        title: "Confirmation",
        description: `Are you sure want to confirm this quotation?`,
        callback: async () => {
          setIsLoading(true);
          const res = await confirmQuotationMutation({
            variables: { id: row.id },
          });
          tableRef.current?.updateRow(res.data?.confirmQuotation as Quotation);
          setIsLoading(false);
          close();
          toast({
            status: "success",
            description: "Confirm Successfully!",
          });
        },
      });
    },
    [close, confirmQuotationMutation, confirmation, setIsLoading, toast]
  );

  const [cancelQuotationMutation] = useCancelQuotationMutation();

  const onCancel = useCallback(
    (row: Quotation) => {
      confirmation({
        title: "Confirmation",
        description: `Are you sure want to cancel this quotation?`,
        callback: async () => {
          setIsLoading(true);
          const res = await cancelQuotationMutation({
            variables: { id: row.id },
          });
          tableRef.current?.updateRow(res.data?.cancelQuotation as Quotation);
          setIsLoading(false);
          close();
          toast({
            status: "success",
            description: "Cancel Successfully!",
          });
        },
      });
    },
    [cancelQuotationMutation, close, confirmation, setIsLoading, toast]
  );

  const tableRef = useRef<TableImperativeHandle<Quotation>>(null);

  const thisYear = getThisYear();

  const queryDownload = useMemo(() => {
    const params: {
      key?: string;
      status?: string;
      dateRangeFrom?: string;
      dateRangeTo?: string;
    } = {
      key: queryParams?.key || undefined,
      status: queryParams?.status || undefined,
    };

    const dateRanges = queryParams?.dateRange?.split("|");

    if (dateRanges && dateRanges.length === 2) {
      params.dateRangeFrom = moment(dateRanges[0]).format("Y-MM-DD");
      params.dateRangeTo = moment(dateRanges[1]).format("Y-MM-DD");
    } else {
      params.dateRangeFrom = moment(thisYear[0]).format("Y-MM-DD");
      params.dateRangeTo = moment(thisYear[1]).format("Y-MM-DD");
    }

    return queryString.stringify(params);
  }, [queryParams, thisYear]);

  const MemozedTable = useMemo(() => {
    return (
      <TableQuotation
        ref={tableRef}
        page={
          queryParams?.page
            ? parseInt(queryParams.page as unknown as string)
            : 1
        }
        onPageChange={onPageChange}
        filterParams={{
          ...queryParams,
          dateRange: queryParams?.dateRange
            ? (queryParams.dateRange
                .split("|")
                .map((elm) => new Date(elm)) as IDateRangeValue)
            : undefined,
        }}
        onFilterSubmit={(filter) => {
          onFilterSubmit({
            ...filter,
            dateRange: filter.dateRange
              ?.map((elm) => moment(elm).format("YYYY-MM-DD"))
              .join("|"),
          });
        }}
        hasMenu
        menuItems={(row) => {
          const options = [
            <MenuItem as={RouterLink} to={`/quotation/${row.id}`}>
              View Detail
            </MenuItem>,
          ];

          if (row.status === QuotationStatus.Hold) {
            options.push(<MenuDivider />);
            options.push(
              <MenuItem
                as={RouterLink}
                onClick={() => {
                  setSelectedId(row.id);
                  onOpenForm();
                }}
              >
                Edit
              </MenuItem>
            );
            options.push(<MenuDivider />);
            options.push(
              <MenuItem onClick={() => onConfirm(row)}>Confirm</MenuItem>
            );
          }

          if (row.status !== QuotationStatus.Canceled) {
            options.push(<MenuDivider />);
            options.push(
              <MenuItem onClick={() => onCancel(row)}>Cancel</MenuItem>
            );
          }

          return options;
        }}
        //onFilterSubmit={onFilterSubmit}
      />
    );
  }, [
    onCancel,
    onConfirm,
    onFilterSubmit,
    onOpenForm,
    onPageChange,
    queryParams,
  ]);

  return (
    <>
      <Container maxW="container.xl">
        <CardForm>
          <TableListTitle
            title="List Quotations"
            // subtitleComponent={
            //   <Flex
            //     color="#717A7A"
            //     fontWeight="500"
            //     fontSize="14px"
            //     lineHeight="1.6"
            //     mt={2}
            //     alignItems="center"
            //     gap={2}
            //   >
            //     <CalendarIcon />
            //     <Box>01 Desember 2024 - 31 Desember 2024</Box>
            //   </Flex>
            // }
            actionComponents={
              <Flex gap={3}>
                <Menu>
                  <MenuButton as={Button} rightIcon={<ChevronDownIcon />}>
                    Export
                  </MenuButton>
                  <MenuList>
                    <MenuItem
                      as={Link}
                      href={`${process.env.REACT_APP_API_URL}/export-quotations-to-pdf?${queryDownload}`}
                    >
                      PDF
                    </MenuItem>
                    <MenuItem
                      as={Link}
                      href={`${process.env.REACT_APP_API_URL}/export-quotations-to-excel?${queryDownload}`}
                    >
                      Excel
                    </MenuItem>
                  </MenuList>
                </Menu>
                <Button onClick={() => onOpenForm()} leftIcon={<AddIcon />}>
                  Create New Quotation
                </Button>
              </Flex>
            }
          />

          {MemozedTable}
        </CardForm>
      </Container>
      <QuotationForm
        isOpen={isOpenForm}
        onClose={(res) => {
          if (res?.type === FormMutationType.UPDATE) {
            tableRef.current?.updateRow(res.data as Quotation);
          } else if (res?.type === FormMutationType.CREATE) {
            tableRef.current?.refresh();
            onPageChange(1);
          }
          setSelectedId("");
          onCloseForm();
        }}
        id={selectedId}
      />
    </>
  );
};

export default ListQuotation;
